// View 8 — Framework Library Management (the editorial library index).
//
// Per design/UI : UX v1.0/for-engineering-frameworks/design-frameworks/
// specs/frameworks-strings.md § View 8. Layout mirrors TenantsPage 1:1:
// PageHeader → stat cards → filter row → table → footer count.
//
// V1 scope:
//   - 4 editorial status chips (All / Draft / Published / Retired)
//   - Domain dropdown filter (reuses the 8-domain enum)
//   - 4 stat cards (TOTAL / PUBLISHED / DRAFT / RETIRED; click filters)
//   - "+ New framework" button → navigates to the editor (B.2) for a new draft
//   - Row click → opens the editor (B.2) for that framework
//
// Out of scope for B.1 (handled by B.2 or later):
//   - The Framework Editor page itself (B.2 ships /admin/knowledge/frameworks/new
//     and /admin/knowledge/frameworks/:id which use the same component)
//   - Cross-tenant adoption health (View 9, Phase D)

import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, StatCard } from '@gc-coach/design-system';
import type {
  Domain,
  FrameworkEditorialStatus,
  FrameworkEditorialStatusFilter,
  FrameworkSummary,
} from '@gc-coach/shared';
import { ALL_DOMAINS } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { useFrameworks } from './useFrameworks.js';
import styles from './FrameworksLibraryPage.module.css';

export function FrameworksLibraryPage() {
  const { t, tt } = useI18n();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<FrameworkEditorialStatusFilter>('all');
  const [domainFilter, setDomainFilter] = useState<Domain | ''>('');

  const query = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(domainFilter ? { domain: domainFilter } : {}),
    }),
    [statusFilter, domainFilter],
  );

  const { data, isLoading } = useFrameworks(query);
  const frameworks = useMemo(() => data?.frameworks ?? [], [data]);
  const counts = data?.counts ?? { total: 0, draft: 0, published: 0, retired: 0 };

  // Marketing empty state — only when there are truly zero frameworks AND no
  // filters narrowing the view. Otherwise show the "no matches" state inside
  // the table so users know it's the filter, not the absence of content.
  const showMarketingEmpty =
    !isLoading && counts.total === 0 && statusFilter === 'all' && !domainFilter;

  const subText = tt('frameworks.librarySubTemplate', {
    total: counts.total,
    published: counts.published,
    draft: counts.draft,
    retired: counts.retired,
  });

  const handleNew = () => {
    // B.2 will mount FrameworkEditorPage at this route. Until then the route
    // is unrouted and the wildcard redirect catches it — acceptable: B.2 is
    // the very next chunk.
    navigate('/admin/knowledge/frameworks/new');
  };

  return (
    <AdminShell
      activeKey="frameworks"
      breadcrumb={[t('frameworks.breadcrumbKnowledge'), t('frameworks.breadcrumbFrameworks')]}
      state={isLoading && !data ? { kind: 'loading' } : { kind: 'normal' }}
    >
      <PageHeader
        title={t('frameworks.libraryTitle')}
        sub={subText}
        actions={
          <Button kind="primary" onClick={handleNew}>
            {t('frameworks.newFrameworkButton')}
          </Button>
        }
      />

      {showMarketingEmpty ? (
        <div className={styles.emptyMarketing}>
          <h2>{t('frameworks.emptyMarketingTitle')}</h2>
          <p>{t('frameworks.emptyMarketingBody')}</p>
          <Button kind="primary" onClick={handleNew}>
            {t('frameworks.newFrameworkButton')}
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.statRow}>
            <StatCard
              label={t('frameworks.statTotal')}
              value={counts.total.toString()}
              onClick={() => setStatusFilter('all')}
            />
            <StatCard
              label={t('frameworks.statPublished')}
              value={counts.published.toString()}
              onClick={() => setStatusFilter('published')}
            />
            <StatCard
              label={t('frameworks.statDraft')}
              value={counts.draft.toString()}
              onClick={() => setStatusFilter('draft')}
            />
            <StatCard
              label={t('frameworks.statRetired')}
              value={counts.retired.toString()}
              onClick={() => setStatusFilter('retired')}
            />
          </div>

          <div className={styles.filterRow}>
            <div className={styles.chips} role="group" aria-label="Status filter">
              <FilterChip
                label={tt('frameworks.chipAllTemplate', { n: counts.total - counts.retired })}
                active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
              />
              <FilterChip
                label={tt('frameworks.chipDraftTemplate', { n: counts.draft })}
                active={statusFilter === 'draft'}
                onClick={() => setStatusFilter('draft')}
              />
              <FilterChip
                label={tt('frameworks.chipPublishedTemplate', { n: counts.published })}
                active={statusFilter === 'published'}
                onClick={() => setStatusFilter('published')}
              />
              <FilterChip
                label={tt('frameworks.chipRetiredTemplate', { n: counts.retired })}
                active={statusFilter === 'retired'}
                onClick={() => setStatusFilter('retired')}
              />
            </div>

            <div className={styles.filterRight}>
              <label className={styles.domainFilter}>
                <span className={styles.domainFilterLabel}>
                  {t('frameworks.domainFilterLabel')}
                </span>
                <select
                  className={styles.domainSelect}
                  value={domainFilter}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setDomainFilter(e.target.value as Domain | '')
                  }
                >
                  <option value="">{t('frameworks.domainFilterAll')}</option>
                  {ALL_DOMAINS.map((d) => (
                    <option key={d} value={d}>
                      {t(`kb.domain.${d}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('frameworks.colTitle')}</th>
                  <th>{t('frameworks.colDomain')}</th>
                  <th>{t('frameworks.colStatus')}</th>
                  <th>{t('frameworks.colVersion')}</th>
                  <th>{t('frameworks.colActivations')}</th>
                  <th>{t('frameworks.colLastEdited')}</th>
                  <th>{t('frameworks.colEditedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {frameworks.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={7} className={styles.empty}>
                      <strong>{t('frameworks.emptyTitle')}</strong>
                      <span>{t('frameworks.emptyBody')}</span>
                    </td>
                  </tr>
                )}
                {frameworks.map((fw) => (
                  <FrameworkRow
                    key={fw.id}
                    framework={fw}
                    onClick={() => navigate(`/admin/knowledge/frameworks/${fw.id}`)}
                  />
                ))}
              </tbody>
            </table>
            <footer className={styles.tableFooter}>
              {tt('frameworks.footerCountTemplate', { n: frameworks.length })}
            </footer>
          </div>
        </>
      )}
    </AdminShell>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function FrameworkRow({
  framework,
  onClick,
}: {
  framework: FrameworkSummary;
  onClick: () => void;
}) {
  const { t } = useI18n();
  return (
    <tr className={styles.row} onClick={onClick} tabIndex={0}>
      <td>
        <div className={styles.cellTitle}>
          <span className={styles.titleText}>{framework.title}</span>
          <span className={styles.titleSub}>{framework.short_description}</span>
        </div>
      </td>
      <td className={styles.cellMono}>{t(`kb.domain.${framework.domain}`)}</td>
      <td className={statusClass(framework.status, styles)}>
        {statusText(framework.status, t)}
      </td>
      <td className={styles.cellMono}>{framework.version}</td>
      <td className={styles.cellMuted}>{framework.activation_count}</td>
      <td className={styles.cellMuted}>{formatDate(framework.updated_at)}</td>
      <td className={styles.cellMuted}>{framework.last_edited_by}</td>
    </tr>
  );
}

// ─── Filter chip ─────────────────────────────────────────────────────────────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[styles.chip, active ? styles.chipActive : null].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

// ─── Status helpers ──────────────────────────────────────────────────────────

function statusClass(
  s: FrameworkEditorialStatus,
  cls: Record<string, string>,
): string {
  if (s === 'draft') return cls['statusDraft'] ?? '';
  if (s === 'published') return cls['statusPublished'] ?? '';
  return cls['statusRetired'] ?? '';
}

function statusText(
  s: FrameworkEditorialStatus,
  t: (k: string) => string,
): string {
  if (s === 'draft') return t('frameworks.statusDraft');
  if (s === 'published') return t('frameworks.statusPublished');
  return t('frameworks.statusRetired');
}

// ─── Date formatter ──────────────────────────────────────────────────────────
//
// Compact date string for the LAST EDITED column. Matches the Tenants page
// formatRelativeTime feel but kept inline here to avoid reaching into another
// feature's helpers. Phase B.2 can extract a shared util if a second framework
// view needs the same formatting.
function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}
